import { FC, useMemo, useRef, useState } from 'react';
import { Controller, FieldError, FormProvider, useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  DatePicker,
  FormInputText,
  InputLabel,
  Modal,
  PhoneNumberInput,
  ProfileImage,
  Select,
  Svg,
  SvgBubble,
  Text,
} from '@hopskipdrive/react-components';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import { InvoiceGroupFormField } from '@rideiq/riders/features/InvoiceGroupDropdown';
import { Button } from '@rideiq/shared/components/Button';
import { colorTokens } from '@rideiq/shared/components/variables';
import { differenceInYears } from 'date-fns';
import { merge } from 'lodash';

import { analyticsClickUploadPhoto } from '../../../../api/services/Analytics';
import { useFlags } from '../../../../contexts/LDContext';
import { useAgeRestrictionPolicy } from '../../../../hooks/useAgeRestrictionPolicy';
import { useCategories } from '../../../../hooks/useCategories';
import { useGenders } from '../../../../hooks/useGenders';
import { GroupedOption } from '../../../../hooks/useLocationOptions';
import { useRiderValidation } from '../../../../hooks/useRiderValidation';
import { useToastStore } from '../../../../shared/stores/toastStore';
import { setFormErrorsForRiderProfile } from '../../../../utils/helpers/setFormErrorsForRiderProfile';
import { DuplicateRiderModal } from '../../../components/DuplicateRiderModal';
import { CAR_SEAT_AUTO_SELECT_MAX_AGE, MIN_CAR_SEAT_AGE } from '../../../constants/riderServices';
import { AideStatus } from '../../../features/RiderAideStatus';
import {
  ConfirmationModal,
  SharedBilling,
  sharedBillingValidationSchema,
  sharedBillingValidationSchemaV2,
} from '../../SharedBilling';
import { SisImportBanner, SisStatus, SisStudentDetails } from '../SisImportBanner';

import {
  ContactPhoto,
  FormField,
  FormRow,
  FullNameError,
  HalfFormField,
  HorizontalDivider,
  ImageWithIcon,
  RiderContact,
  UploadImage,
} from './RiderProfileForm.styles';
import { IRiderProfileFormType } from './types';
import { riderProfileSchema } from './validationSchema';

interface RiderProfileFormProps {
  riderDetails: IRiderProfileFormType & {
    school?: GroupedOption;
  };
  services: RiderService[];
  onSubmit: (formData) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onSisImport?: (sisData: SisStudentDetails) => void;
  sisStatus?: SisStatus;
}

export const RiderProfileForm: FC<RiderProfileFormProps> = ({
  riderDetails,
  services,
  onSubmit,
  setIsSubmitting,
  onSisImport,
  sisStatus,
}) => {
  const { isAdminOrOrganizerUser } = useAuthQuery();
  const { enableApprovalFlowForSharedRider, enableSisImports } = useFlags();
  const schema = riderProfileSchema(isAdminOrOrganizerUser);
  const shareBillingSchema = enableApprovalFlowForSharedRider
    ? sharedBillingValidationSchemaV2()
    : sharedBillingValidationSchema();

  const { control, setValue, watch, reset, handleSubmit, setError, formState, ...rest } = useForm<RiderFormType>({
    defaultValues: useMemo(() => {
      return riderDetails;
    }, [riderDetails]),
    resolver: yupResolver(schema.concat(shareBillingSchema)),
    mode: 'onBlur',
  });
  const { categories } = useCategories();
  const { genders } = useGenders();
  const [formData, setFormData] = useState<RiderFormType | null>(null);
  const [showAgeRestrictionModal, setShowAgeRestrictionModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { getAgeRestrictionPolicyMessage } = useAgeRestrictionPolicy();
  const { validateRiderDetails, isFetching } = useRiderValidation();
  const { addToast } = useToastStore();
  const { isDirty, errors } = formState;
  const [fullNameError, setFullNameError] = useState('');
  const [ageRestrictionMessage, setAgeRestrictionMessage] = useState<string | null>(null);

  const handleSaveRider = async (data: RiderFormType, bypassAgeCheck = false, byPassSharedBillingCheck = false) => {
    // if there is an existing photo and the form object has a new attachment, destroy existing photo
    if (riderDetails?.photoId && data.photosAttributes[0].attachment?.name) {
      const attributes = data.photosAttributes[0] || {};

      data.photosAttributes[0] = merge(attributes, { _destroy: '', id: riderDetails.photoId.toString() });
    }
    const riderAge = data.dateOfBirth && differenceInYears(Date.now(), new Date(data.dateOfBirth));
    const isCSServiceAvailable = services.some((service) => service.code === 'CS' && service.available);

    // destroy photo
    if (!data.photosAttributes[0].attachment && riderDetails?.photoId) {
      const attributes = data.photosAttributes[0] || {};

      data.photosAttributes[0] = merge(attributes, { _destroy: '1', id: riderDetails.photoId.toString() });
    }
    if (riderAge !== null && riderAge < MIN_CAR_SEAT_AGE && !bypassAgeCheck) {
      setShowAgeRestrictionModal(true);
      setAgeRestrictionMessage(getAgeRestrictionPolicyMessage(riderAge));
      setFormData(data);

      return false;
    }

    if (
      riderAge !== null &&
      riderAge >= MIN_CAR_SEAT_AGE &&
      riderAge <= CAR_SEAT_AUTO_SELECT_MAX_AGE &&
      isCSServiceAvailable &&
      !bypassAgeCheck
    ) {
      setShowAgeRestrictionModal(true);
      setAgeRestrictionMessage(getAgeRestrictionPolicyMessage(riderAge));
      setFormData(data);

      return false;
    }

    if (!isDirty) {
      onSubmit(data);

      return true;
    }

    setIsSubmitting(true);
    const abortController = new AbortController();
    const { isSharedBillingRider, sharedBillingRider, ...restOfData } = data;

    const response = await validateRiderDetails({
      ...restOfData,
      ...(isSharedBillingRider ? { sharedBillingRider } : {}),
    });

    if (abortController.signal.aborted) {
      return false;
    }
    if (response.type === 'success') {
      if (data.isSharedBillingRider !== riderDetails.isSharedBillingRider && !byPassSharedBillingCheck) {
        setIsSubmitting(true);
        setShowConfirmationModal(true);
        setFormData(data);

        return false;
      }

      onSubmit({
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth,
        email: data.email,
        mobilePhoneNumber: data.mobilePhoneNumber,
        photosAttributes: data.photosAttributes,
        gender: data.gender,
        category: data.category,
        isAide: data.isAide,
        school: data.school,
        externalId: data.externalId,
        isSharedBillingRider: data.isSharedBillingRider,
        sharedBillingRider: data.sharedBillingRider,
        invoiceGroupId: data.invoiceGroupId,
      });
      setIsSubmitting(false);

      return true;
    }
    const validationErrors = response.data?.errors;

    validationErrors && setFormErrorsForRiderProfile({ setError, validationErrors, setFullNameError, addToast });

    setIsSubmitting(false);

    return false;
  };

  return (
    <FormProvider
      {...{
        handleSubmit,
        setValue,
        errors,
        control,
        watch,
        reset,
        setError,
        formState,
        ...rest,
      }}
    >
      <form
        id="rider-basic-info"
        aria-label="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setFullNameError('');
          handleSubmit((data) => !isFetching && handleSaveRider(data))();
        }}
      >
        {enableSisImports && sisStatus && (
          <SisImportBanner
            sisStatus={sisStatus}
            onImport={(sisData: SisStudentDetails) => {
              // Map SIS data to form fields and reset the form with new values
              // Note: sisData is camelCase (axios transformed from Rails API snake_case)
              reset({
                ...riderDetails,
                firstName: sisData.firstName,
                lastName: sisData.lastName,
                email: sisData.email || '',
                externalId: sisData.identifier,
              });

              // Pass full SIS data to parent for handling caregivers and setting usedSisImport flag
              onSisImport?.(sisData);
            }}
          />
        )}
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="1.5rem">
          <FormRow>
            <FormField>
              <InputLabel fieldtype="required" htmlFor="firstName">
                First name
              </InputLabel>
              <Controller
                name="firstName"
                control={control}
                defaultValue={riderDetails.firstName}
                render={({ field: { value, onChange } }) => (
                  <FormInputText
                    inputProps={{ 'aria-required': true }}
                    id="firstName"
                    value={value}
                    variant="outlined"
                    name="firstName"
                    data-testid="firstName"
                    fullWidth
                    error={fullNameError ? !!(errors.firstName && errors.lastName) : !!errors.firstName}
                    helperText={fullNameError ? '' : errors.firstName?.message}
                    onChange={onChange}
                  />
                )}
              />
            </FormField>

            <FormField>
              <InputLabel fieldtype="required" htmlFor="lastName">
                Last name
              </InputLabel>
              <Controller
                name="lastName"
                control={control}
                defaultValue={riderDetails.lastName}
                render={({ field: { value, onChange } }) => (
                  <FormInputText
                    inputProps={{ 'aria-required': true }}
                    id="lastName"
                    value={value}
                    variant="outlined"
                    name="lastName"
                    data-testid="lastName"
                    fullWidth
                    error={fullNameError ? !!(errors.firstName && errors.lastName) : !!errors.lastName}
                    helperText={fullNameError ? '' : errors.lastName?.message}
                    onChange={onChange}
                  />
                )}
              />
            </FormField>
          </FormRow>

          {fullNameError && (
            <FullNameError>
              <Text textColor="error">{fullNameError}</Text>
            </FullNameError>
          )}

          <FormRow>
            <FormField>
              <InputLabel fieldtype="required" htmlFor="dateOfBirth">
                Date of birth
              </InputLabel>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    onChange={onChange}
                    value={value}
                    slotProps={{
                      textField: {
                        id: 'dateOfBirth',
                        fullWidth: true,
                        error: !!errors.dateOfBirth,
                        helperText: errors.dateOfBirth?.message,
                        inputProps: {
                          'data-testid': 'date-of-birth-picker',
                          'aria-required': true,
                        },
                      },
                    }}
                  />
                )}
              />
            </FormField>

            <FormField>
              <InputLabel fieldtype="required" htmlFor="gender" id="gender-label">
                Gender
              </InputLabel>
              <Controller
                control={control}
                name="gender"
                defaultValue={genders[0]?.value}
                render={({ field: { value, onChange } }) => (
                  <Select
                    id="gender"
                    options={genders}
                    value={genders.length ? value : undefined}
                    setValue={onChange}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <FormInputText
                        {...params}
                        variant="outlined"
                        fullWidth
                        data-testid="gender"
                        error={!!errors.gender}
                        helperText={errors.gender?.message}
                        inputProps={{
                          ...params.inputProps,
                          'aria-required': true,
                          'aria-labelledby': 'gender-label',
                        }}
                      />
                    )}
                  />
                )}
              />
            </FormField>
          </FormRow>

          <HalfFormField>
            <InputLabel fieldtype="required" htmlFor="category" id="category-label">
              Category
            </InputLabel>
            <Controller
              control={control}
              name="category"
              defaultValue={categories[0]?.value}
              render={({ field: { value, onChange } }) => (
                <Select
                  id="category"
                  options={categories}
                  value={categories.length ? value : undefined}
                  setValue={onChange}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <FormInputText
                      {...params}
                      variant="outlined"
                      data-testid="category-form"
                      fullWidth
                      error={!!errors.category}
                      helperText={errors.category?.message}
                      inputProps={{
                        ...params.inputProps,
                        'aria-required': true,
                        'aria-labelledby': 'category-label',
                      }}
                    />
                  )}
                />
              )}
            />
          </HalfFormField>

          <HorizontalDivider />
          <AideStatus />
          <HorizontalDivider />

          <ContactPhoto>
            <RiderContact>
              <Box>
                <InputLabel htmlFor="mobilePhoneNumber">Phone number</InputLabel>
                <Controller
                  control={control}
                  name="mobilePhoneNumber"
                  render={({ field: { onChange, value, name } }) => (
                    <PhoneNumberInput
                      id="mobilePhoneNumber"
                      variant="outlined"
                      name={name}
                      fullWidth
                      data-testid={name}
                      onValueChange={onChange}
                      initialValue={value}
                      error={!!errors.mobilePhoneNumber}
                      helperText={errors.mobilePhoneNumber?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <InputLabel htmlFor="email">Email</InputLabel>
                <Controller
                  name="email"
                  control={control}
                  defaultValue={riderDetails.email}
                  render={({ field: { value, onChange } }) => (
                    <FormInputText
                      id="email"
                      value={value}
                      variant="outlined"
                      name="email"
                      data-testid="email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </Box>
            </RiderContact>
            <FileInput
              control={control}
              riderDetails={riderDetails}
              error={
                errors.photosAttributes?.length &&
                (errors.photosAttributes[0]?.attachment as unknown as FieldError)?.message
              }
            />
          </ContactPhoto>

          <HorizontalDivider />

          <HalfFormField>
            <InputLabel htmlFor="externalId">ID number</InputLabel>
            <Controller
              name="externalId"
              control={control}
              defaultValue={riderDetails.externalId}
              render={({ field: { value, onChange } }) => (
                <FormInputText
                  value={value}
                  variant="outlined"
                  name="externalId"
                  data-testid="idNumber"
                  fullWidth
                  onChange={onChange}
                />
              )}
            />
          </HalfFormField>
          {isAdminOrOrganizerUser && (
            <HalfFormField>
              <InvoiceGroupFormField
                control={control}
                selectDefault
                error={!!errors.invoiceGroupId}
                helperText={errors.invoiceGroupId?.message as string}
              />
            </HalfFormField>
          )}
          <HorizontalDivider />
          <SharedBilling />
        </Box>
        {showAgeRestrictionModal && (
          <Modal open={showAgeRestrictionModal}>
            <Modal.Content>{ageRestrictionMessage}</Modal.Content>
            <Modal.Actions>
              <Button
                color="primary"
                autoFocus
                onClick={() => {
                  setShowAgeRestrictionModal(false);
                  formData && handleSaveRider(formData, true, false);
                }}
              >
                Ok
              </Button>
            </Modal.Actions>
          </Modal>
        )}
        <DuplicateRiderModal />
        <ConfirmationModal
          currentRider={{
            isSharedBillingRider: !!riderDetails?.isSharedBillingRider,
            sharedBillingRider: riderDetails?.sharedBillingRider,
          }}
          needsConfirmation={showConfirmationModal}
          onConfirm={() => {
            setShowConfirmationModal(false);
            formData &&
              handleSaveRider(
                {
                  ...formData,
                  ...(formData.sharedBillingRider
                    ? { sharedBillingRider: { ...formData.sharedBillingRider, confirmed: true } }
                    : {}),
                },
                true,
                true
              );
          }}
          onCancel={() => {
            setShowConfirmationModal(false);
            setIsSubmitting(false);
          }}
          riderStatus={riderDetails?.sharedBillingRider?.status}
        />
      </form>
    </FormProvider>
  );
};

const FileInput = ({ control, riderDetails, error }) => {
  const { field } = useController({ control, name: 'photosAttributes.0.attachment' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const photoUrl =
    (riderDetails.photoId && riderDetails.profilePhotoUrl) || riderDetails.photosAttributes[0].attachment.name
      ? URL.createObjectURL(riderDetails.photosAttributes[0].attachment)
      : '';

  const [preview, setPreview] = useState(photoUrl);
  const PHOTO_SIZE_LIMIT = 10e6;
  const [showFileError, setShowFileError] = useState(false);

  return (
    <Box display="flex" gap="1rem" alignItems="center">
      {preview ? (
        <ImageWithIcon>
          <ProfileImage size={72} imgSrc={preview} />
          <SvgBubble
            height="25px"
            width="25px"
            bgColor={colorTokens.background.default}
            onClick={() => {
              setValue('');
              setPreview('');
              field.onChange('');
            }}
            data-testid="trashcan"
          >
            <Svg icon="trashcan" width="14px" />
          </SvgBubble>
        </ImageWithIcon>
      ) : (
        <Svg width="72px" height="72px" icon="profile-avatar" />
      )}
      <UploadImage>
        <Button
          color="secondary"
          size="small"
          onClick={() => {
            analyticsClickUploadPhoto('Create Rider Wizard');
            fileInputRef?.current?.click();
          }}
        >
          {'Upload New Photo'}
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={fileInputRef}
            hidden
            value={value}
            data-testid="image-upload"
            onChange={(e) => {
              setShowFileError(false);
              setValue(e.target.value);
              if (e.target?.files && e.target?.files[0]) {
                const photoUpload = e.target.files[0];

                if (photoUpload.size > PHOTO_SIZE_LIMIT) {
                  setShowFileError(true);
                } else {
                  setPreview(URL.createObjectURL(photoUpload));
                  field.onChange(e.target.files[0]);
                }
              }
            }}
          />
        </Button>
        <p>Must be a JPG or PNG less than 10MB in size.</p>
        {showFileError && <Text textColor="error">Photo size is greater than 10MB.</Text>}
        {error && <Text textColor="error">{error}</Text>}
      </UploadImage>
    </Box>
  );
};
